import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  ArrowRight, CheckCircle, Activity, BookOpen, 
  Wind, Droplets, MinusCircle, Frown, BatteryLow,
  Calendar, TrendingUp, TrendingDown, Minus, Plus,
  Menu, X, ChevronRight, Sun, Moon, Zap, Star,
  User, Mail, Phone, MessageSquare, Send, Download, FileText
} from "lucide-react";

// Custom logo: Belly with navel and hands forming a heart shape
const BellyHeartLogo = ({ className = "h-8 w-8" }) => (
  <svg viewBox="0 0 40 44" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    {/* Belly silhouette - soft torso curves left and right */}
    <path d="M12 2C10 4 8 10 8 18C8 26 9 32 11 38C12 40 14 42 18 42H22C26 42 28 40 29 38C31 32 32 26 32 18C32 10 30 4 28 2" 
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none" opacity="0.3" />
    {/* Navel dot */}
    <circle cx="20" cy="19" r="1.5" fill="currentColor" opacity="0.45" />
    {/* Left hand - index finger curves up to form left lobe of heart */}
    <path d="M8 24C8 21 11 16 15 15C17.5 14.5 19 15.5 20 17.5" 
      stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" fill="none" />
    {/* Left hand - thumb curves down to bottom point */}
    <path d="M8 24C8 27 12 31 16 32.5C18 33 19.5 33 20 31" 
      stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" fill="none" />
    {/* Right hand - index finger curves up to form right lobe of heart */}
    <path d="M32 24C32 21 29 16 25 15C22.5 14.5 21 15.5 20 17.5" 
      stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" fill="none" />
    {/* Right hand - thumb curves down to bottom point */}
    <path d="M32 24C32 27 28 31 24 32.5C22 33 20.5 33 20 31" 
      stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" fill="none" />
  </svg>
);
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for symptoms
const symptomIcons = {
  wind: Wind, "minus-circle": MinusCircle, droplets: Droplets,
  activity: Activity, "battery-low": BatteryLow, frown: Frown
};

// Section icons for Vitalcheck
const sectionIcons = {
  good_morning: Sun, good_night: Moon, happy_hour: Zap, daily_specials: Star
};

// Navigation Component
const Navigation = ({ currentPage }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <nav className="sticky top-0 z-50 glass border-b border-subtle-border/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src="/images/logo.png" alt="Darmkraftwerk Logo" className="h-14 w-14 object-contain" />
            <span className="font-serif text-xl text-text-primary">Vanessa</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            {[
              { to: "/", label: "Start", page: "home" },
              { to: "/vitalcheck", label: "Vitalcheck", page: "vitalcheck" },
              { to: "/ueber-mich", label: "Über mich", page: "about" },
              { to: "/blog", label: "Ratgeber", page: "blog" },
              { to: "/products", label: "Produkte", page: "products" },
              { to: "/events", label: "Events", page: "events" }
            ].map(item => (
              <Link key={item.page} to={item.to}
                className={`text-sm font-medium transition-colors ${currentPage === item.page ? 'text-brand-primary' : 'text-text-secondary hover:text-brand-primary'}`}
                data-testid={`nav-${item.page}`}>
                {item.label}
              </Link>
            ))}
          </div>
          <button className="md:hidden p-2" onClick={() => setIsOpen(!isOpen)} data-testid="nav-mobile-toggle">
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {isOpen && (
          <div className="md:hidden py-4 border-t border-subtle-border/50 animate-fade-in">
            <div className="flex flex-col gap-4">
              <Link to="/" className="text-sm font-medium text-text-secondary" onClick={() => setIsOpen(false)}>Start</Link>
              <Link to="/vitalcheck" className="text-sm font-medium text-text-secondary" onClick={() => setIsOpen(false)}>Vitalcheck</Link>
              <Link to="/ueber-mich" className="text-sm font-medium text-text-secondary" onClick={() => setIsOpen(false)}>Über mich</Link>
              <Link to="/blog" className="text-sm font-medium text-text-secondary" onClick={() => setIsOpen(false)}>Ratgeber</Link>
              <Link to="/products" className="text-sm font-medium text-text-secondary" onClick={() => setIsOpen(false)}>Produkte</Link>
              <Link to="/events" className="text-sm font-medium text-text-secondary" onClick={() => setIsOpen(false)}>Events</Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer
const Footer = () => (
  <footer className="bg-brand-primary text-white py-12 mt-auto">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <img src="/images/logo.png" alt="Darmkraftwerk Logo" className="h-14 w-14 object-contain" />
            <span className="font-serif text-xl">Vanessa</span>
          </div>
          <p className="text-white/70 text-sm">Ihr Partner für Darmgesundheit und natürliche Nahrungsergänzung.</p>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Navigation</h4>
          <div className="flex flex-col gap-2">
            <Link to="/vitalcheck" className="text-white/70 hover:text-white text-sm transition-colors">Vitalcheck</Link>
            <Link to="/ueber-mich" className="text-white/70 hover:text-white text-sm transition-colors">Über mich</Link>
            <Link to="/blog" className="text-white/70 hover:text-white text-sm transition-colors">Ratgeber</Link>
          </div>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Rechtliches</h4>
          <div className="flex flex-col gap-2">
            <Link to="/impressum" className="text-white/70 hover:text-white text-sm transition-colors">Impressum</Link>
            <Link to="/datenschutz" className="text-white/70 hover:text-white text-sm transition-colors">Datenschutz</Link>
          </div>
        </div>
      </div>
      <div className="border-t border-white/20 mt-8 pt-8 text-center text-white/50 text-sm">
        © 2026 Darmkraftwerk. Alle Rechte vorbehalten.
      </div>
    </div>
  </footer>
);

// Home Page
const Home = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    axios.get(`${API}/blog/articles`).then(r => setArticles(r.data.articles.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation currentPage="home" />
      {/* Full-width banner image */}
      <section className="w-full" data-testid="hero-banner">
        <div className="w-full h-[50vh] sm:h-[60vh] overflow-hidden relative">
          <img 
            src="/images/hero_banner.png" 
            alt="Freiheit und Lebensqualität" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background-primary/30" />
        </div>
      </section>

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0" style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1677751235741-4165995692dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwZ3JlZW4lMjBtb25zdGVyYSUyMGxlYXZlcyUyMHN1bmxpZ2h0fGVufDB8fHx8MTc3NjM2NDgxNXww&ixlib=rb-4.1.0&q=85)`,
          backgroundSize: 'cover', backgroundPosition: 'center right'
        }}>
          <div className="absolute inset-0 bg-gradient-to-r from-background-primary via-background-primary/90 to-background-primary/60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-accent2 mb-4 block animate-fade-in-up">Natürliche Darmgesundheit</span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none text-text-primary mb-6 animate-fade-in-up stagger-1" data-testid="hero-title">
              Ihr Darm ist der Schlüssel zu Ihrem Wohlbefinden
            </h1>
            <p className="text-lg text-text-primary/80 leading-relaxed mb-4 animate-fade-in-up stagger-2">
              Entdecken Sie personalisierte Empfehlungen für Ihre Darmgesundheit. Unser Vitalcheck hilft Ihnen, die richtigen Nahrungsergänzungsmittel zu finden.
            </p>
            <p className="text-lg text-text-primary/80 leading-relaxed mb-8 animate-fade-in-up stagger-2">
              Lassen Sie sich ganz unverbindlich beraten und gehen Sie den ersten Schritt zu mehr Gesundheit & Lebensqualität.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-3">
              <Button onClick={() => navigate('/vitalcheck')} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-full px-8 py-6 text-base font-medium" data-testid="take-quiz-cta">
                Vitalcheck starten <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button variant="outline" onClick={() => navigate('/blog')} className="border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white rounded-full px-8 py-6 text-base font-medium" data-testid="learn-more-cta">
                Mehr erfahren
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-background-secondary" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-accent2 mb-4 block">Unsere Funktionen</span>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-text-primary">Alles für Ihre Darmgesundheit</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: CheckCircle, title: "Vitalcheck", desc: "33 gezielte Fragen aus der Praxis für personalisierte Produktempfehlungen.", color: "bg-brand-accent2/20", iconColor: "text-brand-primary" },
              { icon: Activity, title: "Über mich", desc: "Erfahren Sie mehr über meine Geschichte und meinen Weg zur Darmgesundheit.", color: "bg-brand-accent1/20", iconColor: "text-brand-accent1" },
              { icon: BookOpen, title: "Ratgeber", desc: "Fundierte Artikel rund um Darmgesundheit und Ernährung.", color: "bg-brand-primary/10", iconColor: "text-brand-primary" }
            ].map((f, i) => (
              <Card key={i} className="bg-white rounded-2xl shadow-card card-hover border-0" data-testid={`feature-card-${i}`}>
                <CardContent className="p-8">
                  <div className={`w-12 h-12 rounded-full ${f.color} flex items-center justify-center mb-6`}>
                    <f.icon className={`h-6 w-6 ${f.iconColor}`} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-xl text-text-primary mb-3">{f.title}</h3>
                  <p className="text-text-secondary">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Preview */}
      {articles.length > 0 && (
        <section className="py-24" data-testid="blog-preview-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-end mb-12">
              <div>
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-accent2 mb-4 block">Ratgeber</span>
                <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-text-primary">Aktuelle Artikel</h2>
              </div>
              <Link to="/blog" className="hidden sm:flex items-center text-brand-primary hover:text-brand-primary-hover font-medium" data-testid="view-all-articles">
                Alle Artikel <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {articles.map((article, index) => (
                <Card key={article.id} className="bg-white rounded-2xl shadow-card card-hover border-0 overflow-hidden cursor-pointer" onClick={() => navigate(`/blog/${article.slug}`)} data-testid={`article-card-${index}`}>
                  <div className="aspect-video overflow-hidden">
                    <img src={article.image_url} alt={article.title} className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" />
                  </div>
                  <CardContent className="p-6">
                    <span className="text-xs uppercase tracking-wider text-brand-accent2 font-semibold">{article.category}</span>
                    <h3 className="font-serif text-lg text-text-primary mt-2 mb-2">{article.title}</h3>
                    <p className="text-text-secondary text-sm line-clamp-2">{article.excerpt}</p>
                    <div className="mt-4 text-xs text-text-secondary">{article.read_time} Min. Lesezeit</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 bg-brand-primary" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-6">Bereit für bessere Darmgesundheit?</h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">Starten Sie jetzt unseren kostenlosen Vitalcheck und erhalten Sie personalisierte Empfehlungen.</p>
          <Button onClick={() => navigate('/vitalcheck')} className="bg-white text-brand-primary hover:bg-background-secondary rounded-full px-8 py-6 text-base font-medium" data-testid="cta-start-quiz">
            Jetzt starten <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
      <Footer />
    </div>
  );
};

// Vitalcheck Page
const Vitalcheck = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [currentSectionIdx, setCurrentSectionIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [conditionalTexts, setConditionalTexts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showContact, setShowContact] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [contact, setContact] = useState({ name: '', email: '', phone: '', message: '' });

  useEffect(() => {
    // Reset state on every visit
    setCurrentSectionIdx(0);
    setAnswers({});
    setConditionalTexts({});
    setShowContact(false);
    setResults(null);
    setContact({ name: '', email: '', phone: '', message: '' });
    setLoading(true);
    axios.get(`${API}/vitalcheck/sections`).then(r => {
      setSections(r.data.sections);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleCheckbox = (qId, checked) => {
    setAnswers(prev => ({ ...prev, [qId]: checked }));
    if (!checked) {
      setConditionalTexts(prev => { const n = { ...prev }; delete n[qId]; return n; });
    }
  };

  const handleConditionalText = (qId, text) => {
    setConditionalTexts(prev => ({ ...prev, [qId]: text }));
  };

  const handleNextSection = () => {
    if (currentSectionIdx < sections.length - 1) {
      setCurrentSectionIdx(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setShowContact(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevSection = () => {
    if (showContact) {
      setShowContact(false);
    } else if (currentSectionIdx > 0) {
      setCurrentSectionIdx(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    if (!contact.name || !contact.email) return;
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/vitalcheck/submit`, {
        answers,
        conditional_texts: conditionalTexts,
        contact
      });
      setResults(response.data);
    } catch (e) {
      console.error("Error submitting vitalcheck:", e);
    }
    setSubmitting(false);
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.post(`${API}/vitalcheck/pdf`, {
        answers,
        conditional_texts: conditionalTexts,
        contact
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Vitalcheck_${contact.name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Error downloading PDF:", e);
    }
    setDownloadingPdf(false);
  };

  const totalQuestions = sections.reduce((acc, s) => acc + s.questions.length, 0);
  const questionsUpToCurrent = sections.slice(0, currentSectionIdx + 1).reduce((acc, s) => acc + s.questions.length, 0);
  const progress = showContact ? 100 : sections.length > 0 ? (currentSectionIdx / sections.length) * 100 : 0;

  const currentSection = sections[currentSectionIdx];
  const SectionIcon = currentSection ? (sectionIcons[currentSection.id] || Sun) : Sun;

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col"><Navigation currentPage="vitalcheck" />
        <div className="flex-1 flex items-center justify-center"><div className="animate-pulse text-text-secondary">Laden...</div></div>
      </div>
    );
  }

  // Results view
  if (results) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation currentPage="vitalcheck" />
        <div className="flex-1 py-12" style={{
          backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/03321d81-2f59-49fc-86a3-052795637363/images/f045f1277e7310c7a7ecaeb8aaa780a7d67b04de7562ed3f48c7ed5d88a9136f.png)`,
          backgroundSize: 'cover', backgroundPosition: 'center'
        }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 animate-fade-in-up">
              <CheckCircle className="h-16 w-16 text-success mx-auto mb-4" strokeWidth={1.5} />
              <h1 className="font-serif text-3xl sm:text-4xl text-text-primary mb-4" data-testid="vitalcheck-results-title">
                Vielen Dank!
              </h1>
              <p className="text-text-secondary text-lg leading-relaxed max-w-2xl mx-auto">
                Vielen Dank, dass Sie sich die Zeit genommen haben und den Vitalcheck ausgefüllt haben.
              </p>
              <p className="text-text-secondary text-lg leading-relaxed max-w-2xl mx-auto mt-4">
                Ich werde mich schnellstmöglich persönlich bei Ihnen melden.
              </p>
            </div>

            <div className="mt-12 text-center flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={handleDownloadPdf} disabled={downloadingPdf}
                className="bg-brand-accent1 hover:bg-brand-accent1/90 text-white rounded-full px-6" data-testid="download-pdf-btn">
                {downloadingPdf ? (
                  <><FileText className="mr-2 h-4 w-4 animate-pulse" /> PDF wird erstellt...</>
                ) : (
                  <><Download className="mr-2 h-4 w-4" /> Ergebnisse als PDF herunterladen</>
                )}
              </Button>
              <Button variant="outline" onClick={() => { setResults(null); setShowContact(false); setCurrentSectionIdx(0); setAnswers({}); setConditionalTexts({}); setContact({ name: '', email: '', phone: '', message: '' }); }}
                className="border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white rounded-full" data-testid="restart-vitalcheck">
                Vitalcheck wiederholen
              </Button>
              <Button onClick={() => navigate('/ueber-mich')} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-full" data-testid="go-to-about">
                Mehr über mich
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Contact form view
  if (showContact) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation currentPage="vitalcheck" />
        <div className="flex-1 py-12" style={{
          backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/03321d81-2f59-49fc-86a3-052795637363/images/f045f1277e7310c7a7ecaeb8aaa780a7d67b04de7562ed3f48c7ed5d88a9136f.png)`,
          backgroundSize: 'cover', backgroundPosition: 'center'
        }}>
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <div className="flex justify-between text-sm text-text-secondary mb-2">
                <span>Kontaktdaten</span>
                <span>100%</span>
              </div>
              <Progress value={100} className="h-2 bg-background-secondary" data-testid="vitalcheck-progress" />
            </div>

            <Card className="bg-white rounded-3xl shadow-card border-0 animate-fade-in" data-testid="contact-form-card">
              <CardContent className="p-8 sm:p-12">
                <div className="flex items-center justify-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-brand-primary/10 flex items-center justify-center">
                    <Send className="h-7 w-7 text-brand-primary" strokeWidth={1.5} />
                  </div>
                </div>
                <h2 className="font-serif text-2xl sm:text-3xl text-text-primary mb-2 text-center" data-testid="contact-form-title">
                  Fast geschafft!
                </h2>
                <p className="text-text-secondary text-center mb-8">
                  Bitte hinterlassen Sie Ihre Kontaktdaten, damit wir Ihnen Ihre persönliche Auswertung zusenden können.
                </p>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Name <span className="text-error">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-secondary" strokeWidth={1.5} />
                      <Input value={contact.name} onChange={e => setContact(p => ({ ...p, name: e.target.value }))}
                        placeholder="Ihr vollständiger Name" className="pl-10 rounded-xl border-subtle-border" data-testid="contact-name" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      E-Mail <span className="text-error">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-secondary" strokeWidth={1.5} />
                      <Input type="email" value={contact.email} onChange={e => setContact(p => ({ ...p, email: e.target.value }))}
                        placeholder="ihre.email@beispiel.de" className="pl-10 rounded-xl border-subtle-border" data-testid="contact-email" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Telefon (optional)</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-secondary" strokeWidth={1.5} />
                      <Input type="tel" value={contact.phone} onChange={e => setContact(p => ({ ...p, phone: e.target.value }))}
                        placeholder="+49 123 456789" className="pl-10 rounded-xl border-subtle-border" data-testid="contact-phone" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Nachricht (optional)</label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-text-secondary" strokeWidth={1.5} />
                      <Textarea value={contact.message} onChange={e => setContact(p => ({ ...p, message: e.target.value }))}
                        placeholder="Haben Sie noch Fragen oder Anmerkungen?" className="pl-10 rounded-xl border-subtle-border min-h-[100px]" data-testid="contact-message" />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between mt-8 pt-8 border-t border-subtle-border">
                  <Button variant="ghost" onClick={handlePrevSection} className="text-text-secondary hover:text-brand-primary" data-testid="contact-back-button">
                    Zurück
                  </Button>
                  <Button onClick={handleSubmit} disabled={submitting || !contact.name || !contact.email}
                    className="bg-brand-accent1 hover:bg-brand-accent1/90 text-white rounded-full px-8" data-testid="contact-submit-button">
                    {submitting ? 'Wird gesendet...' : 'Vitalcheck absenden'}
                    {!submitting && <Send className="ml-2 h-4 w-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Quiz sections view
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation currentPage="vitalcheck" />
      <div className="flex-1 py-12" style={{
        backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/03321d81-2f59-49fc-86a3-052795637363/images/f045f1277e7310c7a7ecaeb8aaa780a7d67b04de7562ed3f48c7ed5d88a9136f.png)`,
        backgroundSize: 'cover', backgroundPosition: 'center'
      }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between text-sm text-text-secondary mb-2">
              <span>Abschnitt {currentSectionIdx + 1} von {sections.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2 bg-background-secondary" data-testid="vitalcheck-progress" />
          </div>

          {/* Section Card */}
          <Card className="bg-white rounded-3xl shadow-card border-0 animate-fade-in" data-testid="vitalcheck-section-card">
            <CardContent className="p-6 sm:p-10">
              {/* Section Header */}
              <div className="flex items-center gap-4 mb-8 pb-6 border-b border-subtle-border">
                <div className="w-14 h-14 rounded-full bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                  <SectionIcon className="h-7 w-7 text-brand-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <h2 className="font-serif text-2xl sm:text-3xl text-text-primary" data-testid="section-title">
                    {currentSection?.title}
                  </h2>
                  <p className="text-text-secondary text-sm mt-1">{currentSection?.subtitle}</p>
                </div>
              </div>

              {/* Questions */}
              <div className="space-y-5" data-testid="vitalcheck-questions">
                <p className="text-xs uppercase tracking-[0.15em] font-semibold text-brand-accent2 mb-2">Bitte kreuzen Sie an:</p>
                {currentSection?.questions.map((q, idx) => (
                  <div key={q.id} className="animate-fade-in-up" style={{ animationDelay: `${idx * 0.04}s`, opacity: 0 }}>
                    <div
                      className={`p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                        answers[q.id] ? 'border-brand-primary bg-background-secondary' : 'border-subtle-border hover:border-brand-accent2'
                      }`}
                      onClick={() => handleCheckbox(q.id, !answers[q.id])}
                      data-testid={`question-${q.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          <Checkbox
                            checked={!!answers[q.id]}
                            onCheckedChange={(checked) => handleCheckbox(q.id, checked)}
                            className="border-subtle-border data-[state=checked]:bg-brand-primary data-[state=checked]:border-brand-primary"
                            data-testid={`checkbox-${q.id}`}
                          />
                        </div>
                        <span className="text-sm text-text-primary leading-relaxed">{q.text}</span>
                      </div>
                    </div>
                    {/* Conditional text input */}
                    {q.has_conditional && answers[q.id] && (
                      <div className="ml-10 mt-2 animate-fade-in">
                        <Input
                          value={conditionalTexts[q.id] || ''}
                          onChange={(e) => handleConditionalText(q.id, e.target.value)}
                          placeholder={q.conditional_label}
                          className="rounded-xl border-subtle-border text-sm"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`conditional-${q.id}`}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Navigation */}
              <div className="flex justify-between mt-8 pt-8 border-t border-subtle-border">
                <Button variant="ghost" onClick={handlePrevSection} disabled={currentSectionIdx === 0}
                  className="text-text-secondary hover:text-brand-primary" data-testid="vitalcheck-back-button">
                  Zurück
                </Button>
                <Button onClick={handleNextSection} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-full px-8" data-testid="vitalcheck-next-button">
                  {currentSectionIdx === sections.length - 1 ? 'Weiter zum Kontaktformular' : 'Nächster Abschnitt'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Über mich Page
const AboutMe = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-background-primary">
      <Navigation currentPage="about" />
      <div className="flex-1 py-12 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center mb-12 animate-fade-in-up">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-accent2 mb-4 block">
              Meine Geschichte
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-text-primary" data-testid="about-title">
              Über mich
            </h1>
          </div>

          <div className="flex flex-col lg:flex-row gap-12 items-start">
            {/* Photo */}
            <div className="lg:w-2/5 flex-shrink-0 animate-fade-in-up stagger-1">
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-card">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_gut-health-2/artifacts/79t5yg6q_Foto%20vanessa.jpeg" 
                    alt="Vanessa" 
                    className="w-full object-cover"
                    data-testid="about-photo"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full bg-brand-accent2/20 -z-10" />
                <div className="absolute -top-4 -left-4 w-16 h-16 rounded-full bg-brand-accent1/15 -z-10" />
              </div>
              <div className="text-center mt-6">
                <h2 className="font-serif text-2xl text-text-primary">Vanessa</h2>
                <p className="text-brand-accent2 text-sm font-medium mt-1">Herzensmensch</p>
              </div>
            </div>

            {/* Text */}
            <div className="lg:w-3/5 animate-fade-in-up stagger-2">
              <Card className="bg-white rounded-3xl shadow-card border-0 overflow-hidden">
                <CardContent className="p-8 sm:p-10">
                  <div className="space-y-5 text-text-secondary leading-relaxed" data-testid="about-text">
                    <p>
                      Durch meine familiäre Vorbelastung ist mir das Thema Darmgesundheit in den letzten Jahren immer wichtiger geworden.
                    </p>
                    <p>
                      Ich selber hatte immer weniger Energie, extreme Heißhungerattacken auf Süßes und mit Lebensmittelunverträglichkeiten zu kämpfen.
                    </p>
                    <p className="font-medium text-text-primary">
                      2025 kam für mich der Punkt an dem ich wusste:<br />
                      So kann es nicht weitergehen - ich wollte etwas verändern und wieder mehr Lebensqualität erreichen.
                    </p>
                    <p>
                      Durch Zufall habe ich die Produkte kennen lernen dürfen und ich möchte dies in meinem Alltag nicht mehr missen.
                    </p>
                    <p>
                      Seit der Einnahme fühle ich mich deutlich fitter, ausgeglichener und stressresistenter. Die Heißhungerattacken auf Süßes sind verschwunden und auch meine Unverträglichkeiten gehören mittlerweile der Vergangenheit an.
                    </p>
                    <p>
                      Heute ist es mir wichtig, meine Erfahrungen weiterzugeben und anderen Menschen zu zeigen, wie viel Einfluss die Darmgesundheit auf das persönliche Wohlbefinden haben kann.
                    </p>
                    <p className="text-brand-accent1 font-medium italic text-lg border-l-4 border-brand-accent1 pl-4">
                      Es ist mir eine Herzensangelegenheit auch anderen Menschen wieder zu mehr Lebensqualität zu verhelfen.
                    </p>
                  </div>

                  <div className="mt-8 pt-8 border-t border-subtle-border">
                    <Button 
                      onClick={() => navigate('/vitalcheck')}
                      className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-full px-8"
                      data-testid="about-cta-vitalcheck"
                    >
                      Vitalcheck starten
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Blog Page
const Blog = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { axios.get(`${API}/blog/articles`).then(r => { setArticles(r.data.articles); setLoading(false); }).catch(() => setLoading(false)); }, []);

  if (loading) return (<div className="min-h-screen flex flex-col"><Navigation currentPage="blog" /><div className="flex-1 flex items-center justify-center"><div className="animate-pulse text-text-secondary">Laden...</div></div></div>);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation currentPage="blog" />
      <div className="flex-1 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-accent2 mb-4 block">Ratgeber</span>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-text-primary" data-testid="blog-title">Wissen für Ihre Darmgesundheit</h1>
          </div>

          {/* Intro Section */}
          <div className="max-w-4xl mx-auto mb-16">
            <Card className="bg-white rounded-3xl shadow-card border-0 overflow-hidden">
              <CardContent className="p-8 sm:p-12">
                <p className="text-text-secondary leading-relaxed text-lg mb-8">
                  Ein gesunder Darm ist die Grundlage für Energie, Wohlbefinden und ein starkes Immunsystem. Viele Beschwerden wie Müdigkeit, Verdauungsprobleme, Hautprobleme, Blähungen oder Unverträglichkeiten können mit einer gestörten Darmbalance zusammenhängen.
                </p>

                <h2 className="font-serif text-2xl sm:text-3xl text-text-primary mb-4" data-testid="blog-section-why">
                  Warum ist die Darmgesundheit so wichtig
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Der Darm ist ein zentrales Organ für die Gesundheit und beeinflusst weit mehr als nur die Verdauung. Er spielt eine wichtige Rolle für das Immunsystem, ist ein "zweites Gehirn", produziert Hormone, reguliert den Wasserhaushalt und versorgt den Körper durch Nährstoffaufnahme mit Energie.
                </p>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Eine gestörte Darmflora, fachsprachlich Dysbiose genannt, entsteht, wenn das Gleichgewicht zwischen nützlichen und schädlichen Bakterien im Darm gestört ist. Dies kann durch verschiedene Faktoren ausgelöst werden, wobei oft eine Kombination aus Lebensstil, äußeren Einflüssen, körperlichen Faktoren und Erkrankungen verantwortlich ist. Beispiele hierfür sind einseitige Ernährung, Medikamente, Stress und Darminfektionen.
                </p>

                <h2 className="font-serif text-2xl sm:text-3xl text-text-primary mt-10 mb-6" data-testid="blog-section-problems">
                  Hauptprobleme einer gestörten Darmflora
                </h2>
                <div className="space-y-5">
                  {[
                    { title: "Verdauungsbeschwerden", desc: "Häufig Blähungen, Blähbauch, Krämpfe, Verstopfung oder chronischer Durchfall" },
                    { title: "Geschwächtes Immunsystem", desc: "Erhöhte Infektanfälligkeit, da Krankheitserreger leichter die Darmwand passieren können" },
                    { title: "Nährstoffmangel & Unverträglichkeiten", desc: "Nahrungsmittelunverträglichkeiten (z.B. Milchzucker) und Verdauungsstörungen (Malabsorption)" },
                    { title: "Allgemeine Symptome", desc: "Müdigkeit, Leistungsminderung, Kopfschmerzen, Hautprobleme, depressive Verstimmungen und Heißhungerattacken" },
                    { title: "Stuhlveränderungen", desc: "Abnorme Farben, übler Geruch oder Schleimbeimengungen" }
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4 items-start p-4 rounded-xl bg-background-secondary" data-testid={`problem-item-${i}`}>
                      <div className="w-2 h-2 rounded-full bg-brand-accent1 mt-3 flex-shrink-0" />
                      <div>
                        <h3 className="font-serif text-xl text-text-primary">{item.title}</h3>
                        <p className="text-text-secondary text-base mt-1 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Articles Section */}
          <div className="text-center mb-10">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-accent2 mb-4 block">Artikel</span>
            <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-text-primary">Weitere Informationen</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article, index) => (
              <Card key={article.id} className="bg-white rounded-2xl shadow-card card-hover border-0 overflow-hidden cursor-pointer animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => navigate(`/blog/${article.slug}`)} data-testid={`blog-article-${index}`}>
                <div className="aspect-video overflow-hidden"><img src={article.image_url} alt={article.title} className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" /></div>
                <CardContent className="p-6">
                  <span className="text-xs uppercase tracking-wider text-brand-accent2 font-semibold">{article.category}</span>
                  <h2 className="font-serif text-xl text-text-primary mt-2 mb-3">{article.title}</h2>
                  <p className="text-text-secondary text-sm line-clamp-3">{article.excerpt}</p>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-subtle-border">
                    <span className="text-xs text-text-secondary">{article.read_time} Min. Lesezeit</span>
                    <span className="text-brand-primary text-sm font-medium flex items-center">Lesen <ChevronRight className="h-4 w-4 ml-1" /></span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Blog Article Page
const BlogArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { axios.get(`${API}/blog/articles/${slug}`).then(r => { setArticle(r.data); setLoading(false); }).catch(() => setLoading(false)); }, [slug]);

  if (loading) return (<div className="min-h-screen flex flex-col"><Navigation currentPage="blog" /><div className="flex-1 flex items-center justify-center"><div className="animate-pulse text-text-secondary">Laden...</div></div></div>);
  if (!article) return (<div className="min-h-screen flex flex-col"><Navigation currentPage="blog" /><div className="flex-1 flex items-center justify-center"><div className="text-center"><h2 className="font-serif text-2xl text-text-primary mb-4">Artikel nicht gefunden</h2><Button onClick={() => navigate('/blog')} className="bg-brand-primary text-white rounded-full">Zurück zum Blog</Button></div></div></div>);

  const renderContent = (content) => {
    return content.trim().split('\n').map((line, i) => {
      if (line.startsWith('# ')) return <h1 key={i} className="font-serif text-3xl text-text-primary mb-6">{line.slice(2)}</h1>;
      if (line.startsWith('## ')) return <h2 key={i} className="font-serif text-2xl text-text-primary mt-8 mb-4">{line.slice(3)}</h2>;
      if (line.startsWith('- ')) return <li key={i} className="text-text-secondary ml-6 mb-2" dangerouslySetInnerHTML={{ __html: line.slice(2).replace(/\*\*(.*?)\*\*/g, '<strong class="text-text-primary">$1</strong>') }} />;
      if (line.match(/^\d\./)) return <li key={i} className="text-text-secondary ml-6 mb-2 list-decimal">{line.slice(3)}</li>;
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="text-text-secondary leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-text-primary">$1</strong>') }} />;
    });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation currentPage="blog" />
      <article className="flex-1 py-12" data-testid="blog-article">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate('/blog')} className="flex items-center text-brand-primary hover:text-brand-primary-hover mb-8 transition-colors" data-testid="back-to-blog">
            <ChevronRight className="h-4 w-4 mr-1 rotate-180" /> Zurück zum Blog
          </button>
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-accent2 mb-4 block">{article.category}</span>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-text-primary mb-6" data-testid="article-title">{article.title}</h1>
          <div className="flex items-center gap-4 text-text-secondary text-sm mb-8">
            <span>{article.read_time} Min. Lesezeit</span><span>·</span>
            <span>{new Date(article.published_at).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          </div>
          <div className="aspect-video rounded-2xl overflow-hidden mb-12"><img src={article.image_url} alt={article.title} className="w-full h-full object-cover" /></div>
          <div className="prose max-w-none">{renderContent(article.content)}</div>
          <div className="mt-12 pt-8 border-t border-subtle-border">
            <h3 className="font-serif text-xl text-text-primary mb-4">Nächste Schritte</h3>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={() => navigate('/vitalcheck')} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-full" data-testid="article-cta-quiz">Vitalcheck machen</Button>
              <Button variant="outline" onClick={() => navigate('/products')} className="border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white rounded-full" data-testid="article-cta-products">Produkte entdecken</Button>
            </div>
          </div>
        </div>
      </article>
      <Footer />
    </div>
  );
};

// Products Page
const Products = () => {
  const navigate = useNavigate();

  const optimalProducts = [
    {
      id: "powercocktail",
      name: "FitLine PowerCocktail",
      subtitle: "Baustein 1",
      image: "/images/powercocktail.png",
      badges: ["NTC", "Made in Germany"],
      benefits: [
        { title: "Darmreinigung", desc: "Unterstützt die natürliche Reinigung des Darms" },
        { title: "Grundversorgung mit Nähr-Vital-Ballaststoffen", desc: "Versorgt den Körper mit essentiellen Nährstoffen und Ballaststoffen" },
        { title: "Energiekick", desc: "Für mehr Energie und Vitalität im Alltag" }
      ]
    },
    {
      id: "feelgood",
      name: "FitLine Feel Good",
      subtitle: "Baustein 2 - Joghurt-Drink",
      image: "/images/feelgood.png",
      badges: [],
      benefits: [
        { title: "Darmaufbau", desc: "Unterstützt den Aufbau einer gesunden Darmflora" },
        { title: "Vier größten Darmbakterienstämme", desc: "Enthält die vier wichtigsten Bakterienstämme für den Darm" },
        { title: "Milliarden von positiven Darmbakterien", desc: "Hochdosierte probiotische Kulturen" }
      ]
    },
    {
      id: "restorate",
      name: "FitLine Restorate",
      subtitle: "Baustein 3 - Citrus & Exotic",
      image: "/images/restorate.png",
      badges: ["NTC", "Made in Germany"],
      benefits: [
        { title: "Säure-Basen-Haushalt", desc: "Reguliert das Gleichgewicht im Körper" },
        { title: "Bessere Regeneration und erholsamer Schlaf", desc: "Unterstützt die Erholung über Nacht" },
        { title: "Mineralien, Spurenelemente & Vitamin D", desc: "Umfassende Mineralstoffversorgung" }
      ]
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation currentPage="products" />
      <div className="flex-1 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Headline - unchanged */}
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-accent2 mb-4 block">Nahrungsergänzungsmittel</span>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-text-primary" data-testid="products-title">Unsere Produkte</h1>
          </div>

          {/* Optimalversorgung Section */}
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-text-primary" data-testid="products-optimal-title">
              Unsere Optimalversorgung
            </h2>
            <p className="text-text-secondary mt-3 max-w-2xl mx-auto">Die drei Bausteine für Ihre Darmgesundheit</p>
          </div>

          {/* Three Products - Original Presentation Slides */}
          <div className="space-y-10">
            {optimalProducts.map((product, index) => (
              <div key={product.id} className="animate-fade-in-up" style={{ animationDelay: `${index * 0.15}s` }} data-testid={`optimal-product-${index}`}>
                <Card className="bg-white rounded-3xl shadow-card border-0 overflow-hidden">
                  <CardContent className="p-0">
                    <img 
                      src={product.image} 
                      alt={product.name} 
                      className="w-full h-auto"
                    />
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-16 text-center bg-background-secondary rounded-3xl p-12">
            <h2 className="font-serif text-2xl sm:text-3xl text-text-primary mb-4">Finden Sie Ihre persönliche Empfehlung</h2>
            <p className="text-text-secondary mb-6 max-w-xl mx-auto">Unser Vitalcheck hilft Ihnen, die passenden Produkte für Ihre Bedürfnisse zu finden.</p>
            <Button onClick={() => navigate('/vitalcheck')} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-full px-8" data-testid="products-quiz-cta">
              Zum Vitalcheck <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};


// Impressum Page
const Impressum = () => (
  <div className="min-h-screen flex flex-col">
    <Navigation currentPage="" />
    <div className="flex-1 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-serif text-4xl tracking-tight text-text-primary mb-8" data-testid="impressum-title">Impressum</h1>
        <Card className="bg-white rounded-3xl shadow-card border-0">
          <CardContent className="p-8 sm:p-10 space-y-6">
            <div>
              <h2 className="font-serif text-xl text-text-primary mb-3">Angaben gemäß § 5 DDG</h2>
              <p className="text-text-secondary leading-relaxed">
                Vanessa Biesinger<br />
                Silcherstraße 13<br />
                86368 Gersthofen
              </p>
            </div>
            <div>
              <h2 className="font-serif text-xl text-text-primary mb-3">Kontakt</h2>
              <p className="text-text-secondary leading-relaxed">
                Telefon: 0176 55179232<br />
                E-Mail: info@darmkraftwerk.com
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    <Footer />
  </div>
);



// Datenschutz Page
const Datenschutz = () => (
  <div className="min-h-screen flex flex-col">
    <Navigation currentPage="" />
    <div className="flex-1 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-serif text-4xl tracking-tight text-text-primary mb-8" data-testid="datenschutz-title">Datenschutz</h1>
        <Card className="bg-white rounded-3xl shadow-card border-0">
          <CardContent className="p-8 sm:p-10 space-y-8 text-text-secondary leading-relaxed">
            <p>
              Wir freuen uns über Ihr Interesse an unserer Website. Der Schutz Ihrer Privatsphäre ist für uns sehr wichtig. Nachstehend informieren wir Sie ausführlich über den Umgang mit Ihren Daten.
            </p>

            <div>
              <h2 className="font-serif text-xl text-text-primary mb-3">1. Zugriffsdaten und Hosting</h2>
              <p className="mb-4">
                Sie können unsere Webseiten besuchen, ohne Angaben zu Ihrer Person zu machen. Bei jedem Aufruf einer Webseite speichert der Webserver lediglich automatisch ein sogenanntes Server-Logfile, das z.B. den Namen der angeforderten Datei, Ihre IP-Adresse, Datum und Uhrzeit des Abrufs, übertragene Datenmenge und den anfragenden Provider (Zugriffsdaten) enthält und den Abruf dokumentiert.
              </p>
              <p className="mb-4">
                Diese Zugriffsdaten werden ausschließlich zum Zwecke der Sicherstellung eines störungsfreien Betriebs der Seite sowie der Verbesserung unseres Angebots ausgewertet. Dies dient gemäß Art. 6 Abs. 1 S. 1 lit. f DSGVO der Wahrung unserer im Rahmen einer Interessensabwägung überwiegenden berechtigten Interessen an einer korrekten Darstellung unseres Angebots. Alle Zugriffsdaten werden spätestens sieben Tage nach Ende Ihres Seitenbesuchs gelöscht.
              </p>
              <h3 className="font-semibold text-text-primary mb-2">Hostingdienstleistungen durch einen Drittanbieter</h3>
              <p>
                Im Rahmen einer Verarbeitung in unserem Auftrag erbringt ein Drittanbieter für uns die Dienste zum Hosting und zur Darstellung der Webseite. Dies dient der Wahrung unserer im Rahmen einer Interessensabwägung überwiegenden berechtigten Interessen an einer korrekten Darstellung unseres Angebots. Alle Daten, die im Rahmen der Nutzung dieser Webseite oder in dafür vorgesehenen Formularen wie folgend beschrieben erhoben werden, werden auf seinen Servern verarbeitet. Eine Verarbeitung auf anderen Servern findet nur in dem hier erläuterten Rahmen statt.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl text-text-primary mb-3">2. Datenerhebung und -verwendung</h2>
              <p>
                Wir erheben personenbezogene Daten, wenn Sie uns diese im Rahmen einer Kontaktaufnahme mit uns (z.B. per Kontaktformular oder E-Mail) mitteilen. Pflichtfelder werden als solche gekennzeichnet, da wir in diesen Fällen die Daten zwingend zur Bearbeitung Ihrer Kontaktaufnahme benötigen und Sie ohne deren Angabe die Kontaktaufnahme nicht versenden können. Welche Daten erhoben werden, ist aus den jeweiligen Eingabeformularen ersichtlich. Wir verwenden die von ihnen mitgeteilten Daten gemäß Art. 6 Abs. 1 S. 1 lit. b DSGVO zur Bearbeitung Ihrer Anfragen. Nach vollständiger Abwicklung werden Ihre Daten für die weitere Verarbeitung eingeschränkt und nach Ablauf etwaiger steuer- und handelsrechtlichen Aufbewahrungsfristen gelöscht, sofern Sie nicht ausdrücklich in eine weitere Nutzung Ihrer Daten eingewilligt haben oder wir uns eine darüber hinausgehende Datenverwendung vorbehalten, die gesetzlich erlaubt ist und über die wir Sie in dieser Erklärung informieren.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl text-text-primary mb-3">3. SSL- bzw. TLS-Verschlüsselung</h2>
              <p>Diese Seite nutzt eine verschlüsselte Verbindung zum Schutz vertraulicher Inhalte.</p>
            </div>

            <div>
              <h2 className="font-serif text-xl text-text-primary mb-3">4. Datenweitergabe</h2>
              <p>Ihre Daten werden an keinen weitergeben.</p>
            </div>

            <div>
              <h2 className="font-serif text-xl text-text-primary mb-3">5. Kontaktmöglichkeiten und Ihre Rechte</h2>
              <p className="mb-4">Als Betroffener haben Sie folgende Rechte:</p>
              <ul className="list-disc ml-6 space-y-2 mb-4">
                <li>gemäß Art. 15 DSGVO das Recht, in dem dort bezeichneten Umfang Auskunft über Ihre von uns verarbeiteten personenbezogenen Daten zu verlangen;</li>
                <li>gemäß Art. 16 DSGVO das Recht, unverzüglich die Berichtigung unrichtiger oder Vervollständigung Ihrer bei uns gespeicherten personenbezogenen Daten zu verlangen;</li>
                <li>gemäß Art. 17 DSGVO das Recht, die Löschung Ihrer bei uns gespeicherten personenbezogenen Daten zu verlangen, soweit nicht die weitere Verarbeitung erforderlich ist:
                  <ul className="list-['-_'] ml-4 mt-1 space-y-1">
                    <li>zur Ausübung des Rechts auf freie Meinungsäußerung und Information;</li>
                    <li>zur Erfüllung einer rechtlichen Verpflichtung;</li>
                    <li>aus Gründen des öffentlichen Interesses oder</li>
                    <li>zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen;</li>
                  </ul>
                </li>
                <li>gemäß Art. 18 DSGVO das Recht, die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen, soweit
                  <ul className="list-['-_'] ml-4 mt-1 space-y-1">
                    <li>die Richtigkeit der Daten von Ihnen bestritten wird;</li>
                    <li>die Verarbeitung unrechtmäßig ist, Sie aber deren Löschung ablehnen;</li>
                    <li>wir die Daten nicht mehr benötigen, Sie diese jedoch zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen benötigen oder</li>
                    <li>Sie gemäß Art. 21 DSGVO Widerspruch gegen die Verarbeitung eingelegt haben;</li>
                  </ul>
                </li>
                <li>gemäß Art. 20 DSGVO das Recht, Ihre personenbezogenen Daten, die Sie uns bereitgestellt haben, in einem strukturierten, gängigen und maschinenlesebaren Format zu erhalten oder die Übermittlung an einen anderen Verantwortlichen zu verlangen;</li>
                <li>gemäß Art. 77 DSGVO das Recht, sich bei einer Aufsichtsbehörde zu beschweren. In der Regel können Sie sich hierfür an die Aufsichtsbehörde Ihres üblichen Aufenthaltsortes oder Arbeitsplatzes oder unseres Unternehmenssitzes wenden.</li>
              </ul>
              <p>
                Bei Fragen zur Erhebung, Verarbeitung oder Nutzung Ihrer personenbezogenen Daten, bei Auskünften, Berichtigung, Sperrung oder Löschung von Daten sowie Widerruf erteilter Einwilligungen oder Widerspruch gegen eine bestimmte Datenverwendung wenden Sie sich bitte direkt an uns über die Kontaktdaten in unserem Impressum.
              </p>
            </div>

            <div className="border-2 border-brand-primary/20 rounded-2xl p-6 bg-brand-primary/5">
              <h2 className="font-serif text-xl text-text-primary mb-3">Widerspruchsrecht</h2>
              <p className="mb-4">
                Soweit wir zur Wahrung unserer im Rahmen einer Interessensabwägung überwiegenden berechtigten Interessen personenbezogene Daten wie oben erläutert verarbeiten, können Sie dieser Verarbeitung mit Wirkung für die Zukunft widersprechen. Erfolgt die Verarbeitung zu Zwecken des Direktmarketings, können Sie dieses Recht jederzeit wie oben beschrieben ausüben. Soweit die Verarbeitung zu anderen Zwecken erfolgt, steht Ihnen ein Widerspruchsrecht nur bei Vorliegen von Gründen, die sich aus Ihrer besonderen Situation ergeben, zu.
              </p>
              <p className="mb-4">
                Nach Ausübung Ihres Widerspruchsrechts werden wir Ihre personenbezogenen Daten nicht weiter zu diesen Zwecken verarbeiten, es sei denn, wir können zwingende schutzwürdige Gründe für die Verarbeitung nachweisen, die Ihre Interessen, Rechte und Freiheiten überwiegen, oder wenn die Verarbeitung der Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen dient.
              </p>
              <p>
                Dies gilt nicht, wenn die Verarbeitung zu Zwecken des Direktmarketings erfolgt. Dann werden wir Ihre personenbezogenen Daten nicht weiter zu diesem Zweck verarbeiten.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    <Footer />
  </div>
);


// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Force remount Vitalcheck on every navigation
const VitalcheckWrapper = () => {
  const { key } = useLocation();
  return <Vitalcheck key={key} />;
};



// Events Page
const Events = () => {
  const events = [
    {
      id: 1,
      date: "06.06.2026",
      time: "11:00 Uhr",
      location: "Tenless EMS Friedberg",
      street: "Ludwigstraße 34",
      city: "86316 Friedberg"
    },
    {
      id: 2,
      date: "25.06.2026",
      time: "19:00 Uhr",
      location: "Gasthof Stern",
      street: "Kirchplatz 10",
      city: "86368 Gersthofen"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation currentPage="events" />
      <div className="flex-1 py-12 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-accent2 mb-4 block">
              Veranstaltungen
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-text-primary mb-4" data-testid="events-title">
              Events
            </h1>
            <p className="text-text-secondary text-lg max-w-2xl mx-auto">
              Kostenlose Infoveranstaltungen: Unverbindlich. Informativ. Inspirierend.
            </p>
          </div>

          <div className="space-y-8">
            {events.map((event, index) => (
              <Card key={event.id} className="bg-white rounded-3xl shadow-card border-0 overflow-hidden animate-fade-in-up" style={{ animationDelay: `${index * 0.15}s` }} data-testid={`event-card-${index}`}>
                <CardContent className="p-0">
                  <div className="flex flex-col sm:flex-row">
                    <div className="sm:w-48 bg-brand-primary flex flex-col items-center justify-center p-6 text-white">
                      <span className="text-3xl font-serif font-bold">{event.date.split('.')[0]}</span>
                      <span className="text-sm uppercase tracking-wider mt-1">
                        {['', 'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'][parseInt(event.date.split('.')[1])]} {event.date.split('.')[2]}
                      </span>
                      <span className="text-sm mt-2 opacity-80">{event.time}</span>
                    </div>
                    <div className="flex-1 p-6 sm:p-8 flex flex-col justify-center">
                      <h3 className="font-serif text-xl text-text-primary mb-2">{event.location}</h3>
                      <p className="text-text-secondary">
                        {event.street}<br />
                        {event.city}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="bg-brand-primary/5 border border-brand-primary/10 rounded-3xl mt-12" data-testid="events-contact">
            <CardContent className="p-8 sm:p-10 text-center">
              <h2 className="font-serif text-2xl text-text-primary mb-4">Anmeldung</h2>
              <p className="text-text-secondary mb-6">
                Melden Sie sich gerne per Mail, WhatsApp oder telefonisch an:
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a href="mailto:info@darmkraftwerk.com" className="flex items-center gap-2 text-brand-primary hover:text-brand-primary-hover font-medium transition-colors" data-testid="events-email">
                  <Mail className="h-5 w-5" />
                  info@darmkraftwerk.com
                </a>
                <span className="hidden sm:block text-subtle-border">|</span>
                <a href="tel:+4917655179232" className="flex items-center gap-2 text-brand-primary hover:text-brand-primary-hover font-medium transition-colors" data-testid="events-phone">
                  <Phone className="h-5 w-5" />
                  0176 55179232
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};


function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/vitalcheck" element={<VitalcheckWrapper />} />
          <Route path="/ueber-mich" element={<AboutMe />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/products" element={<Products />} />
          <Route path="/events" element={<Events />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
